/**
 * 퀘스트 스팟 관리자 바 — 숨은 조작 UI (오른쪽 아래 5회 터치 → 비밀번호 → 타이틀바).
 *
 * 왜 숨기나 (사용자 요청 2026-07-30)
 *   이 태블릿은 **고객 앞**에 놓인다. 기존 상단 헤더(← 메뉴 / 제목 / 지점명)는 고객에게
 *   불필요하고, 아이가 눌러 화면을 벗어나는 사고가 난다. 그래서 헤더를 전부 없애고 운영자
 *   조작을 이 컴포넌트 뒤로 감춘다.
 *
 * 디자인 출처 (Figma ZOOLUNG-ZOOLUNG-WEBAPP-PROJECT)
 *   · 타이틀바      node 1966:44176 (TITLEBAR)
 *   · 비밀번호 모달 node 1965:48860 (Modal)
 *   ⚠️ 디자인 폰트는 Pretendard Variable 이지만 tools 에는 없다(`public/fonts` = andong-katuri
 *      뿐). 새 웹폰트를 임의로 들이지 않고 tools 기본 스택을 쓰되, **크기·굵기·색·간격·라운드는
 *      디자인 수치를 그대로** 따른다. 디자인은 1920×1200 기준이라 1600px 미만에서 축소한다
 *      (기존 `QuestTagPage.css` 의 1600px 브레이크포인트와 같은 경계).
 *
 * 🔴 '미션변경' 의 의미 (사용자 확인)
 *   콤보에서 고른 미션으로 **이동**하고 이 오버레이를 닫는다. 그래서 콤보 선택은 **보류
 *   (staged)** 상태로 두고, 이 버튼에서 `onApplySpot` 으로 확정한다. 고르는 즉시 적용하면
 *   실수로 고른 미션이 바로 반영된다.
 *
 * 🔴 보안 경계가 아니다. 4자리 고정 PIN 은 고객·아이의 **우발적 진입을 막는 장치**일 뿐이며
 *    작정한 사람을 막지 못한다(코드에 상수로 들어 있다). 실제 권한 통제는 tools 로그인이 한다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import icoArrowLeft from '../../assets/quest-spot/arrow-left.svg';
import icoArrowDown from '../../assets/quest-spot/arrow-down.svg';
import icoSaveExit from '../../assets/quest-spot/save-exit.svg';
import icoEye from '../../assets/quest-spot/eye.svg';
import './QuestSpotAdminBar.css';

/** 사용자 지정 기본 비밀번호. 바꾸려면 이 상수를 고쳐 재배포한다(하드코드 — 사용자 결정). */
export const ADMIN_PASSWORD = '2580';

/** 오른쪽 아래를 몇 번 눌러야 열리는지. */
export const TAP_TARGET_COUNT = 5;

/**
 * 터치 유효 시간 창(ms). 이 창을 벗어난 옛 터치는 버린다.
 * 🔴 없으면 고객이 며칠에 걸쳐 우연히 누른 터치가 누적돼 어느 순간 열린다.
 *    반대로 너무 짧으면 운영자가 5번을 못 채운다 — 3초는 5회 탭에 충분한 여유다.
 */
export const TAP_WINDOW_MS = 3000;

/**
 * 터치 1회 기록. 시간 창을 벗어난 앞선 터치를 잘라낸 새 배열을 돌려준다(불변).
 *
 * @param {number[]|null} taps 기존 타임스탬프 목록
 * @param {number} now 이번 터치 시각(ms)
 * @returns {number[]}
 */
export function pushTap(taps, now) {
  const prev = Array.isArray(taps) ? taps : [];
  return [...prev.filter((t) => now - t <= TAP_WINDOW_MS), now];
}

/** 제스처 완성 여부. */
export function isTapComplete(taps) {
  return Array.isArray(taps) && taps.length >= TAP_TARGET_COUNT;
}

/**
 * 비밀번호 판정. 앞뒤 공백만 허용하고 **정확히 일치**해야 한다.
 * 🔴 빈 문자열이 통과하면 [확인] 만 눌러도 열리므로 반드시 막는다.
 */
export function checkPassword(raw) {
  return String(raw ?? '').trim() === ADMIN_PASSWORD;
}

/**
 * 중앙 라벨 접두. 디자인은 `미션스팟 No.1` 이지만 **번호를 붙이지 않는다**(사용자 요청 2026-07-30).
 * 🔴 스팟 데이터에 번호 필드가 없어(실측: id·name·description·mission_type·reward_type) 번호는
 *    목록 순번으로 만들 수밖에 없는데, 스팟이 추가·삭제되면 **같은 스팟의 번호가 바뀐다.**
 *    운영자가 "3번 스팟" 으로 기억하면 어긋나므로 아예 표기하지 않는다.
 */
export const SPOT_LABEL = '미션스팟';

/** 보류 중인 미션 선택을 확정하는 관리자 바 버튼 문구. */
export const MISSION_CHANGE_LABEL = '미션변경';

/**
 * 보류 중인 콤보 선택 → 실제 스팟. 보류값이 없거나 목록에서 사라졌으면 **현재 적용된 스팟**으로
 * 되돌린다(없는 스팟으로 이동하는 사고 방지).
 *
 * @param {Array} spots
 * @param {number|string|null} stagedId `<select>` 값은 문자열로 나오므로 문자열 비교
 * @param {object|null} currentSpot
 */
export function stagedSpotOf(spots, stagedId, currentSpot) {
  const list = Array.isArray(spots) ? spots : [];
  if (stagedId != null && stagedId !== '') {
    const found = list.find((s) => s && String(s.id) === String(stagedId));
    if (found) return found;
  }
  return currentSpot || null;
}

const MISSION_TYPE_KR = {
  ox_quiz: 'OX 퀴즈',
  picture_quiz: '그림 퀴즈',
  funny_photo: '사진 찍기',
  qr: '태깅',
};

/**
 * 콤보 옵션 한 줄 — **미션명(+종류)만**. `No.N` 을 붙이지 않는다(위 SPOT_LABEL 주석 참조).
 * 이름이 없으면 빈 문자열 — 옵션에 `undefined` 가 뜨는 것을 막는다.
 */
export function spotOptionLabel(spot) {
  const name = spot && spot.name ? String(spot.name) : '';
  if (!name) return '';
  const kind = MISSION_TYPE_KR[spot.mission_type];
  return kind ? `${name} (${kind})` : name;
}

/** 호출부가 사유를 주지 않았는데 고를 것이 없을 때의 잠금 문구(방어값). */
export const SPOTS_EMPTY_LOCK = '미션 목록 없음';

/**
 * 화면에 띄울 잠금 문구. **빈 문자열이면 변경 가능**이다(문구가 곧 판정).
 *
 * 🔴 왜 boolean 이 아니라 문구인가 (2026-08-07)
 *   예전에는 `canPick` boolean 이라 "왜 막혔는지"를 잃었다. 미션선택은 `opacity:0` 투명
 *   `<select>` 라서 `disabled` 가 붙어도 **화면이 그대로**고, 커서 없는 태블릿에서는 시각
 *   신호가 0 이었다 → 운영자가 "터치 고장" 으로 보고했다. 판정과 사유를 **한 값**으로 묶어
 *   둘이 갈라질 여지를 없앤다.
 *
 * @param {string|null|undefined} lockReason 페이지가 내려준 사유(없으면 변경 가능)
 * @param {Array} spots 선택 가능한 스팟 목록
 * @returns {string} 잠금 문구, 또는 잠기지 않았으면 `''`
 */
export function lockTextOf(lockReason, spots) {
  const reason = String(lockReason ?? '').trim();
  if (reason) return reason;
  // 호출부가 사유를 빠뜨려도 옵션 없는 콤보가 열리지 않게 한다.
  return (Array.isArray(spots) && spots.length > 0) ? '' : SPOTS_EMPTY_LOCK;
}

/** PIN 최대 길이. 진행 표시(●) 개수와 실제 값이 어긋나지 않게 입력을 여기서 끊는다. */
export const PIN_MAX_LEN = 8;

/** 키패드 배열 — 전화기 배열(1~9, 전체삭제, 0, 한 자 지우기). */
export const KEYPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'];

/**
 * 키패드 한 번 누름을 현재 PIN 에 적용한다.
 *
 * 🔴 OS 키보드를 쓰지 않는 이유: 기존 `type="password" + inputMode="numeric"` 은 안드로이드가
 *    `type=password` 에서 `inputMode` 를 무시해 **전체 문자 키보드**를 띄웠다(사용자 실측).
 *    키오스크에서 OS 키보드는 화면을 가리고 기기·WebView 마다 동작이 갈리므로 앱에서 그린다.
 *
 * @param {string} pin 현재 값
 * @param {string} key 숫자 1자 | 'back' | 'clear'
 * @returns {string}
 */
export function applyKeypadKey(pin, key) {
  const cur = typeof pin === 'string' ? pin : '';
  if (key === 'clear') return '';
  if (key === 'back') return cur.slice(0, -1);
  // 숫자 **한 자**만 허용 — 그 밖의 값은 무시해 PIN 이 오염되지 않게 한다.
  if (typeof key !== 'string' || !/^[0-9]$/.test(key)) return cur;
  return cur.length >= PIN_MAX_LEN ? cur : cur + key;
}

const PHASE_IDLE = 'idle';
const PHASE_PASSWORD = 'password';
const PHASE_BAR = 'bar';

/**
 * @param {object}   props
 * @param {Array}    props.spots         선택 가능한 스팟 목록
 * @param {object}   props.selectedSpot  현재 적용된 스팟
 * @param {string|null} props.lockReason 변경 불가 사유. null 이면 변경 가능(→ `lockTextOf`)
 * @param {Function} props.onApplySpot   (spot) => void — 보류 선택 확정. 저장은 호출부가 한다
 * @param {Function} props.onExitHome    () => void — tools 홈으로 나가기
 * @param {Function} props.onOpenChange  (open:boolean) => void — 오버레이 열림/닫힘 통지
 */
export default function QuestSpotAdminBar({
  spots, selectedSpot, lockReason, onApplySpot, onExitHome, onOpenChange,
}) {
  const [phase, setPhase] = useState(PHASE_IDLE);
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwVisible, setPwVisible] = useState(false);
  const [stagedId, setStagedId] = useState(null);

  // 터치 이력은 렌더에 쓰지 않으므로 ref — state 로 두면 탭마다 리렌더가 돌아 스캔 화면이 흔들린다.
  const tapsRef = useRef([]);

  /**
   * 🔴 오버레이가 떠 있음을 페이지에 알린다 (2026-08-07).
   *   이 통지가 없어서 페이지는 바가 열린 줄 모르고 QR 스캔을 계속 받았다. 운영자가 바를
   *   열어둔 사이 고객이 태그하면 미션이 **바 아래에서 보이지 않게** 시작되고, 그 상태
   *   변화가 미션선택을 잠가 "가끔 터치가 안 된다" 로 보고됐다.
   *   비밀번호 모달 단계부터 '열림' 이다 — 그 시점에 이미 운영자가 조작 중이다.
   */
  const isOpen = phase !== PHASE_IDLE;
  useEffect(() => { onOpenChange?.(isOpen); }, [isOpen, onOpenChange]);

  // 잠금 판정과 사유는 한 값이다 — `''` 이면 변경 가능.
  const lockText = lockTextOf(lockReason, spots);

  const openPassword = useCallback(() => {
    tapsRef.current = [];
    setPw('');
    setPwError('');
    setPwVisible(false);
    setPhase(PHASE_PASSWORD);
  }, []);

  const handleCornerTap = useCallback(() => {
    tapsRef.current = pushTap(tapsRef.current, Date.now());
    if (isTapComplete(tapsRef.current)) openPassword();
  }, [openPassword]);

  const closeAll = useCallback(() => {
    tapsRef.current = [];
    setPhase(PHASE_IDLE);
    setStagedId(null);
    setPw('');
    setPwError('');
  }, []);

  const submitPassword = useCallback(() => {
    if (!checkPassword(pw)) {
      setPwError('비밀번호가 올바르지 않습니다.');
      setPw('');
      return;
    }
    setStagedId(selectedSpot ? String(selectedSpot.id) : null);
    setPw('');
    setPwError('');
    setPhase(PHASE_BAR);
  }, [pw, selectedSpot]);

  const staged = useMemo(
    () => stagedSpotOf(spots, stagedId, selectedSpot),
    [spots, stagedId, selectedSpot],
  );

  // 🔴 '미션변경' = 보류 선택 확정 + 오버레이 닫기. 같은 스팟이면 적용을 건너뛴다
  //    (같은 스팟 재적용은 스캔 상태를 괜히 초기화한다).
  const handleMissionChange = useCallback(() => {
    if (staged && (!selectedSpot || staged.id !== selectedSpot.id)) onApplySpot?.(staged);
    closeAll();
  }, [staged, selectedSpot, onApplySpot, closeAll]);

  return (
    <>
      {/* 🔴 보이지 않는 터치존. 디자인(node 1965:48746)에는 이 자리에 `관리자 모드` 필이 있으나
          지시가 "오른쪽 아래를 5번 터치" 라 **숨은 제스처**로 둔다. 필을 보이게 하려면
          .qsab-corner 에 배경·텍스트를 넣으면 된다. */}
      {phase === PHASE_IDLE && (
        <button
          type="button"
          className="qsab-corner"
          aria-label="관리자 모드"
          onClick={handleCornerTap}
          data-testid="quest-admin-corner"
        />
      )}

      {phase === PHASE_PASSWORD && (
        <div className="qsab-backdrop" data-testid="quest-admin-password">
          <div className="qsab-modal" role="dialog" aria-modal="true" aria-label="관리자 비밀번호">
            <div className="qsab-modal-body">
              <div className="qsab-modal-heads">
                <p className="qsab-modal-title">관리자 비밀번호를 입력해 주세요.</p>
                <p className="qsab-modal-sub">현재 화면은 주렁주렁 동물원 관리자만 사용해 주세요.</p>
              </div>
              {/* 🔴 OS 키보드를 띄우지 않는다 — `<input>` 을 쓰지 않고 진행 표시만 그린다.
                  안드로이드는 type=password 에서 inputMode 를 무시해 문자 키보드를 띄우고,
                  키오스크에서는 그 키보드가 화면을 가린다(사용자 실측 2026-07-30). */}
              <div
                className={`qsab-input-box${pwError ? ' qsab-input-box-error' : ''}`}
                role="status"
                aria-label={`비밀번호 ${pw.length}자 입력됨`}
                data-testid="quest-admin-password-display"
              >
                <div className="qsab-dots">
                  {pw.length === 0 && <span className="qsab-dots-ph">비밀번호 입력</span>}
                  {pwVisible
                    ? <span className="qsab-dots-plain">{pw}</span>
                    : pw.split('').map((_, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <span key={i} className="qsab-dot" />
                    ))}
                </div>
                <button
                  type="button"
                  className="qsab-eye"
                  onClick={() => setPwVisible((v) => !v)}
                  aria-label={pwVisible ? '비밀번호 숨기기' : '비밀번호 보기'}
                >
                  <img src={icoEye} alt="" />
                </button>
              </div>

              {/* 앱 자체 숫자 키패드 — 전화기 배열. 태블릿 터치를 고려해 크게 잡는다. */}
              <div className="qsab-keypad" data-testid="quest-admin-keypad">
                {KEYPAD_KEYS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`qsab-key${k === 'clear' || k === 'back' ? ' qsab-key-fn' : ''}`}
                    onClick={() => { setPw((v) => applyKeypadKey(v, k)); setPwError(''); }}
                    aria-label={k === 'clear' ? '전체 지우기' : (k === 'back' ? '한 자 지우기' : k)}
                    data-testid={`quest-admin-key-${k}`}
                  >
                    {k === 'clear' ? 'C' : (k === 'back' ? '⌫' : k)}
                  </button>
                ))}
              </div>
              {/* 실패는 조용히 넘기지 않는다 — 운영자가 왜 안 열리는지 알아야 한다. */}
              {pwError && <p className="qsab-modal-error" role="alert">{pwError}</p>}
            </div>
            <div className="qsab-modal-btns">
              <button type="button" className="qsab-btn qsab-btn-cancel" onClick={closeAll}>
                취소
              </button>
              <button
                type="button"
                className="qsab-btn qsab-btn-ok"
                onClick={submitPassword}
                data-testid="quest-admin-password-ok"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === PHASE_BAR && (
        <div className="qsab-bar" data-testid="quest-admin-bar">
          <button type="button" className="qsab-bar-back" onClick={onExitHome}>
            <img src={icoArrowLeft} alt="" className="qsab-ico" />
            <span>뒤로가기</span>
          </button>

          <div className={`qsab-bar-center${lockText ? ' qsab-bar-center-locked' : ''}`}>
            <span className="qsab-bar-no">{SPOT_LABEL}</span>
            <span className="qsab-bar-div">|</span>
            <span className="qsab-bar-name">{staged ? staged.name : '미션 미선택'}</span>
            {/* 🔴 잠겼으면 **이유를 보여주고 ▼ 를 감춘다.** 예전에는 disabled 만 붙어 화면이
                그대로였고(투명 select), 운영자에게는 터치 고장으로 보였다. ▼ 가 남아 있으면
                "열 수 있다" 는 신호가 되므로 함께 감춘다. */}
            {lockText ? (
              <span className="qsab-bar-lock" role="status" data-testid="quest-admin-lock-reason">
                {lockText}
              </span>
            ) : (
              <img src={icoArrowDown} alt="" className="qsab-ico" data-testid="quest-admin-arrow" />
            )}
            {/* 네이티브 select 를 투명하게 덮는다 — 태블릿에서 OS 기본 피커가 떠 터치가 가장 편하고,
                커스텀 드롭다운보다 화면 밖으로 잘리는 문제가 없다. */}
            <select
              className="qsab-bar-select"
              value={staged ? String(staged.id) : ''}
              onChange={(e) => setStagedId(e.target.value)}
              disabled={!!lockText}
              aria-label="미션 선택"
              data-testid="quest-admin-spot-select"
            >
              {!staged && <option value="">미션 선택</option>}
              {/* 라벨에 `No.N` 을 넣지 않는다 — 목록 순번 기반이라 스팟 추가·삭제 시 값이 바뀐다. */}
              {(spots || []).map((s) => (
                <option key={s.id} value={String(s.id)}>{spotOptionLabel(s)}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="qsab-bar-save"
            onClick={handleMissionChange}
            data-testid="quest-admin-save-exit"
          >
            <span>{MISSION_CHANGE_LABEL}</span>
            <img src={icoSaveExit} alt="" className="qsab-ico" />
          </button>
        </div>
      )}
    </>
  );
}
